import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

/**
 * Выбор одного из списка.
 *
 * **Нативный `<select>` не используется** — это правило кита, которое
 * нельзя нарушать. Вместо него `.wrapctl` + `.ctl` + `.pop`.
 * Поиск есть в любом списке, сколько бы ни было пунктов (кит 2.15).
 *
 * **Внутри модального окна поповер не ставится** — конфликт слоёв
 * (ТЗ §7). В окнах вместо него ChoiceGroup, список `.opt`
 * или поле с поиском.
 */

export interface Option {
  value: string;
  label: string;
  /** Правая приписка пункта: счётчик, дата, пояснение */
  side?: string;
  /** Заголовок группы `.gr` — пункты одной группы идут подряд */
  group?: string;
}

interface PopoverSelectProps {
  value: string | null;
  options: Option[];
  onChange: (value: string) => void;
  placeholder?: string;
  /** Последнему элементу в ряду нужен `.rt`, иначе поповер вылезет за экран */
  alignRight?: boolean;
  /** Вид поля формы: контур и ширина колонки вместо серой плашки фильтра */
  asField?: boolean;
  icon?: string;
  disabled?: boolean;
}

/** Телефон — ширина мобильного слоя кита (до 760 px) */
const isPhone = () => window.matchMedia('(max-width: 760px)').matches;

/**
 * Поиск в выпадающем списке `.pop .srch` (кит 2.15): в любом списке, сколько
 * бы ни было пунктов. На десктопе фокус в поле сразу; на телефоне нет —
 * шторка открывается без клавиатуры, искать — нажатием на поле.
 */
export function PopSearch({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <input
      className="srch"
      placeholder="Поиск"
      value={value}
      autoFocus={!isPhone()}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** Пункты, в подписи которых есть набранное, без учёта регистра */
export function filterOptions<T extends { label: string }>(options: T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
}

/** Ничего не нашлось по запросу — в отличие от пустого списка */
export function PopNotFound() {
  return (
    <div className="none">
      <b>Ничего не нашлось</b>
      Попробуйте другое слово
    </div>
  );
}

export function PopoverSelect({
  value,
  options,
  onChange,
  placeholder = '—',
  alignRight,
  asField,
  icon,
  disabled,
}: PopoverSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrap = useRef<HTMLDivElement>(null);

  const current = options.find((o) => o.value === value);

  const visible = useMemo(() => filterOptions(options, query), [options, query]);

  useEffect(() => {
    if (!open) return;

    const close = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  return (
    <div className="wrapctl" ref={wrap} style={asField ? { display: 'block' } : undefined}>
      <div
        className={`ctl${open ? ' act' : ''}`}
        style={asField ? { width: '100%' } : undefined}
        onClick={() => {
          if (disabled) return;
          setQuery('');
          setOpen((v) => !v);
        }}
      >
        {icon ? (
          <svg>
            <use href={`#${icon}`} />
          </svg>
        ) : null}
        <span>{current?.label ?? placeholder}</span>
        <svg style={{ marginLeft: 'auto' }}>
          <use href="#chev" />
        </svg>
      </div>

      {open ? (
        <div className={alignRight ? 'pop rt' : 'pop'}>
          <PopSearch value={query} onChange={setQuery} />

          {visible.length ? (
            visible.map((o, i) => [
              o.group && o.group !== visible[i - 1]?.group ? (
                <div className="gr" key={`g:${o.group}`}>
                  {o.group}
                </div>
              ) : null,
              <div
                key={o.value}
                className={o.value === value ? 'op on' : 'op'}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.label}
                {o.side ? <span className="sb">{o.side}</span> : null}
              </div>,
            ])
          ) : (
            <PopNotFound />
          )}
        </div>
      ) : null}
    </div>
  );
}

/** Поповер внутри поля формы: лейбл сверху, контур и ширина колонки. */
export function SelectField({
  label,
  hint,
  ...rest
}: PopoverSelectProps & { label: string; hint?: ReactNode }) {
  return (
    <div className="field">
      <span className="lbl">{label}</span>
      <PopoverSelect {...rest} asField />
      {hint ? <span className="sub">{hint}</span> : null}
    </div>
  );
}
